// biome-ignore lint/style/noRestrictedImports: encapsulation
import type { EditorProps as MonacoEditorProps } from "@monaco-editor/react";
import React from "react";

import { useFormFieldContext } from "~/components/form/use-form-field-context";
import { SkeletonBox } from "~/components/skeleton/box";
import { cv } from "~/tools/class-variance";
import { flattenString } from "~/tools/flatten-string";
import { useScoped } from "~/tools/impulse";
import { isString } from "~/tools/is-string";
import { Logger } from "~/tools/logger";
import { useHandler } from "~/tools/use-handler";

import { DEFAULT_EDITOR_APPEARANCE, type EditorOptions, useEditorOptions } from "./_hooks/use-editor-options";
import { useOnEditorBlur } from "./_hooks/use-on-editor-blur";
import { type EditorLanguage, LanguageLoader } from "./_languages/language-loader";
import type { HeightSpec } from "./_utils/calculate-auto-height";
import { enforceSingleLine } from "./_utils/enforce-single-line";
import { getHeightProp } from "./_utils/get-height-prop";
import { preventTabulation } from "./_utils/prevent-tabulation";
import styles from "./_code.module.css";
import { EditorAutoHeight } from "./_editor-auto-height";
import { loader } from "./_loader";
import type { Monaco } from "./_monaco";

const logger = Logger.namespace("~/components/code/editor");

// biome-ignore lint/style/noRestrictedImports: encapsulation
const MonacoEditor = React.lazy(() => import("@monaco-editor/react"));

const variants = cv(styles.editor, {
	variants: {
		appearance: {
			text: styles.appearanceText,
			code: null,
		},
	},

	defaultVariants: {
		appearance: DEFAULT_EDITOR_APPEARANCE,
	},
});

export interface EditorProps
	extends Omit<EditorOptions, "disableLinks" | "glyphMargin" | "inDiffEditor" | "quickSuggestions">,
		Pick<MonacoEditorProps, "className" | "width" | "value"> {
	/**
	 * When "auto" the editor will automatically adjust its height based on the content.
	 *
	 * The `string * {}` allows the "auto" value to suggest in IDE as a specific type,
	 * while still allowing any string value to be passed.
	 */
	height?: HeightSpec | number | "auto" | (string & {});
	language: EditorLanguage;
	onBlur?: VoidFunction;
	onChange?: (value: string) => void;
	onPressEnter?: (event: Monaco.IKeyboardEvent) => void;
}

export const Editor = React.forwardRef<undefined | Monaco.editor.IStandaloneCodeEditor, EditorProps>(
	(
		{
			className,
			size,
			height = "auto",
			width,
			language,
			value,
			readOnly,
			placeholder,
			disableLineNumbers,
			disableRuler,
			disableLineHighlight,
			appearance = DEFAULT_EDITOR_APPEARANCE,
			onChange,
			onBlur,
			onPressEnter,
			...props
		},
		ref,
	) => {
		const [monaco, setMonaco] = React.useState<null | typeof Monaco>(null);
		const [editor, setEditor] = React.useState<null | Monaco.editor.IStandaloneCodeEditor>(null);
		const formField = useFormFieldContext();
		const submitting = useScoped((scope) => formField?.state.isSubmitting(scope), [formField?.state]);
		const [staticHeight, dynamicHeight] = getHeightProp(height);

		const formattedValue = React.useMemo(
			() => (appearance === "input" && value ? flattenString(value) : value),
			[value, appearance],
		);

		React.useEffect(() => {
			const loadMonaco = async () => {
				try {
					setMonaco(await loader());
				} catch (error) {
					logger.error("Failed to load Monaco editor:", error);
				}
			};

			void loadMonaco();
		}, []);

		// stabilize the onChange so Editor does not re-subscribe to the monaco editor on every change
		const stableOnChange = useHandler<[string]>((text = "") => onChange?.(text));
		const stableOnPressEnter = useHandler(onPressEnter);

		const options = useEditorOptions(editor, {
			size,
			readOnly: readOnly || submitting,
			placeholder,
			disableLineNumbers,
			disableRuler,
			disableLineHighlight,
			appearance,
			quickSuggestions: isString(language),
		});

		useOnEditorBlur(editor, () => {
			onBlur?.();
			// mark the field as touched on blur
			formField?.state.setTouched(true);
		});

		React.useEffect(() => {
			if (editor) {
				// focus the editor on the FormImpulse.focusFirstInvalid call
				return formField?.state.onFocusWhenInvalid(() => {
					editor.focus();
				});
			}
		}, [editor, formField?.state]);

		React.useEffect(() => {
			if (monaco && editor) {
				const cleanups = [
					appearance === "input" ? enforceSingleLine(monaco, editor) : null,
					appearance !== "code" ? preventTabulation(monaco, editor) : null,
				];

				return () => {
					for (const cleanup of cleanups) {
						cleanup?.();
					}
				};
			}
		}, [monaco, editor, appearance]);

		// assign on Enter key press
		React.useEffect(() => {
			if (monaco && editor && stableOnPressEnter) {
				const disposable = editor.onKeyDown((event) => {
					if (event.keyCode === monaco.KeyCode.Enter) {
						stableOnPressEnter(event);
					}
				});

				return () => {
					disposable.dispose();
				};
			}
		}, [monaco, editor, stableOnPressEnter]);

		React.useImperativeHandle(ref, () => editor ?? undefined, [editor]);

		return (
			<EditorAutoHeight borderWidth={1} editor={editor} height={dynamicHeight} monaco={monaco}>
				<React.Suspense fallback={<SkeletonBox />}>
					{monaco && (
						<MonacoEditor
							className={variants({
								appearance: appearance === "code" ? "code" : "text",
								className,
							})}
							height={staticHeight}
							language={isString(language) ? language : language.name}
							loading={<SkeletonBox />}
							onChange={stableOnChange}
							onMount={setEditor}
							options={options}
							value={formattedValue}
							width={width}
							{...props}
						/>
					)}
				</React.Suspense>
				{monaco && editor && <LanguageLoader editor={editor} language={language} monaco={monaco} />}
			</EditorAutoHeight>
		);
	},
);
